import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Button,
    TextField,
    Alert,
    Paper,
    IconButton,
    Snackbar,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from "@mui/material";
import { useNavigate } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LogoutIcon from '@mui/icons-material/Logout';
import LinkIcon from '@mui/icons-material/Link';
import SearchIcon from '@mui/icons-material/Search';
import { getCardByUniqueCode, generateCardLink, isAuthenticated, updateCardStatus } from '../api/adminApi';
import { useAuth } from '../Context/AuthContext';
import '../CSS/AdminDashboard.css';

const AdminDashboard = () => {
    const [uniqueCode, setUniqueCode] = useState('');
    const [cardData, setCardData] = useState(null);
    const [generatedLink, setGeneratedLink] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const navigate = useNavigate();
    const { logout } = useAuth();

    useEffect(() => {
        // Check if user is authenticated
        if (!isAuthenticated()) {
            navigate('/admin/login');
        }
    }, [navigate]);

    const handleSearch = async () => {
        if (!uniqueCode || uniqueCode.trim() === '') {
            setError('Please enter a unique code');
            return;
        }

        setError('');
        setLoading(true);
        setCardData(null);
        setGeneratedLink('');

        try {
            const data = await getCardByUniqueCode(uniqueCode);
            setCardData(data);
        } catch (err) {
            setError(err.message || 'Failed to fetch card');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateLink = async () => {
        if (!uniqueCode) {
            setError('Please search for a card first');
            return;
        }

        setError('');
        setLoading(true);

        try {
            const response = await generateCardLink(uniqueCode);
            setGeneratedLink(response.fullUrl);
        } catch (err) {
            setError(err.message || 'Failed to generate link');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(generatedLink);
            setCopySuccess(true);
        } catch (err) {
            setError('Failed to copy link');
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/admin/login');
    };

    const handleStatusChange = async (e) => {
        const isPaid = e.target.value === 'ACTIVE'; // ACTIVE means paid
        setError('');
        setLoading(true);
        try {
            const updated = await updateCardStatus(uniqueCode, isPaid);
            setCardData(updated);
            setGeneratedLink('');
        } catch (err) {
            setError(err.message || 'Failed to update status');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    return (
        <div className="admin-dashboard-container">
            <Box className="admin-dashboard-header">

                <Typography variant="h5" className="admin-dashboard-title">
                    Admin Portal
                </Typography>
                <IconButton
                    onClick={handleLogout}
                    className="admin-logout-btn"
                    title="Logout"
                >
                    <LogoutIcon />
                </IconButton>
            </Box>

            <Paper className="admin-dashboard-content" elevation={3}>
                <Typography variant="h6" gutterBottom className="section-title">
                    Generate Wedding Card Link
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ marginBottom: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Unique Code Search */}
                <Box className="search-section">
                    <TextField
                        fullWidth
                        label="Unique Code"
                        variant="outlined"
                        value={uniqueCode}
                        onChange={(e) => setUniqueCode(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Enter 6-character code (e.g., X7K2P9)"
                        disabled={loading}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                        }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleSearch}
                        disabled={loading || !uniqueCode}
                        className="search-btn"
                        sx={{
                            backgroundColor: "#55C2C3",
                            "&:hover": { backgroundColor: "#48A6A7" },
                            textTransform: "none",
                            marginTop: 2,
                        }}
                    >
                        {loading ? 'Searching...' : 'Search Card'}
                    </Button>
                </Box>

                {/* Card Details */}
                {cardData && (
                    <Box className="card-details-section">
                        <Typography variant="subtitle1" className="card-details-title">
                            Card Details
                        </Typography>
                        <Box className="card-info">
                            <div className="info-row">
                                <strong>Code:</strong> <span>{cardData.uniqueCode}</span>
                            </div>
                            <div className="info-row">
                                <strong>Couple:</strong> <span>{cardData.cardData?.greetingName}</span>
                            </div>
                            <div className="info-row">
                                <strong>Event:</strong> <span>{cardData.cardData?.eventType}</span>
                            </div>
                            <div className="info-row">
                                <strong>Date:</strong> <span>{cardData.cardData?.eventDate}</span>
                            </div>
                        </Box>

                        <FormControl sx={{ minWidth: 200, marginTop: 2 }} size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={cardData.status === 'ACTIVE' ? 'ACTIVE' : 'PENDING PAYMENT'}
                                label="Status"
                                onChange={handleStatusChange}
                                disabled={loading}
                            >
                                <MenuItem value="ACTIVE">✅ Active (Link Live)</MenuItem>
                                <MenuItem value="PENDING PAYMENT">⏳ Pending Payment (Not Live)</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Warning if not active */}
                        {cardData.status !== 'ACTIVE' && (
                            <Alert severity="warning" sx={{ marginTop: 2 }}>
                                Card must be set to <strong>Active</strong> before generating a link.
                            </Alert>
                        )}

                        <Button
                            variant="contained"
                            onClick={handleGenerateLink}
                            disabled={loading || cardData.status !== 'ACTIVE'}
                            startIcon={<LinkIcon />}
                            className="generate-btn"
                            sx={{
                                backgroundColor: "#55C2C3",
                                "&:hover": { backgroundColor: "#48A6A7" },
                                textTransform: "none",
                                marginTop: 2,
                                "&:disabled": { backgroundColor: "#e5e8eb", color: "#919eab" }
                            }}
                        >
                            {loading ? 'Processing...' : 'Generate Link'}
                        </Button>
                    </Box>
                )}

                {/* Generated Link */}
                {generatedLink && (
                    <Box className="generated-link-section">
                        <Typography variant="subtitle1" className="link-title">
                            Generated Link
                        </Typography>
                        <Box className="link-display">
                            <Typography variant="body2" className="link-text">
                                {generatedLink}
                            </Typography>
                            <IconButton
                                onClick={handleCopyLink}
                                className="copy-btn"
                                title="Copy to clipboard"
                            >
                                <ContentCopyIcon />
                            </IconButton>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ marginTop: 1 }}>
                            Copy this link and send it to the customer
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Copy Success Snackbar */}
            <Snackbar
                open={copySuccess}
                autoHideDuration={3000}
                onClose={() => setCopySuccess(false)}
                message="Link copied to clipboard!"
            />
        </div>
    );
};

export default AdminDashboard;
